import { FcmMessageDataInterface, StoreWrapperInterface, STORE_WRAPPER_TOKEN, ApplicationsEnum } from '@actassa/api';
import { Inject, Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { isEmpty, isEqual } from 'lodash-es';
import { combineLatest, from, Observable, Subscription } from 'rxjs';
import { take, tap, filter, switchMap, map, distinctUntilChanged, catchError, delay } from 'rxjs/operators';

import { RoutesDictionary } from '../dictionaries/routes.dictionary';
import { FCMPushDataActions } from '../enums/fcm-push-data-actions.enum';

import { DataCollectionsStoreService } from './data-collections-store.service';
import { InspectionsService } from './inspections.service';

type pushHelper = (data: FcmMessageDataInterface) => void;

@Injectable({
    providedIn: 'root',
})
export class PushHandlerService {
    private generatePushHelper: Record<FCMPushDataActions, pushHelper> = {
        [FCMPushDataActions.NEW_DATA_COLLECTION]: this.dataCollection,
    };
    private subscriptions: Subscription;

    constructor(
        @Inject(STORE_WRAPPER_TOKEN) private storeWrapper: StoreWrapperInterface,
        private readonly dataCollectionService: InspectionsService,
        private readonly navigationController: NavController,
        private readonly dbService: DataCollectionsStoreService,
    ) {
        this.isActionSubscriptionEnabled$
            .pipe(
                distinctUntilChanged(),
                tap((isAuthenticated: boolean) => {
                    if (isAuthenticated) {
                        const initSubscription = this.init$().subscribe();

                        this.subscriptions = new Subscription();
                        this.subscriptions.add(initSubscription);

                        return;
                    }

                    this.subscriptions?.unsubscribe();
                }),
            )
            .subscribe();
    }

    private init$(): Observable<Array<number>> {
        return combineLatest([
            this.storeWrapper.notificationData$,
            this.storeWrapper.isAppStateActive$,
            this.storeWrapper.providerId$,
        ])
            .pipe(
                /**
                 * Хак, который исправляет проблему отображения лоадера
                 * (видать не все успевают проинициализироваться)
                 */
                delay(0),
                filter(([data, isAppActive, providerId]: [FcmMessageDataInterface, boolean, string]) =>
                    !isEmpty(data) && isAppActive && this.checkValidProviderId(data, providerId)),
                map(([data]: [FcmMessageDataInterface, boolean, string]) => data),
                distinctUntilChanged(isEqual),
                filter((data: FcmMessageDataInterface) => this.isActionHandled(data)),
                tap(() => this.storeWrapper.loadingStart()),
                switchMap((data: FcmMessageDataInterface) => this.preloadDataCollections$()
                    .pipe(
                        tap(() => this.handle(data)),
                    )),
                tap(() => this.storeWrapper.loadingEnd()),
                catchError(() => {
                    this.storeWrapper.loadingEnd();

                    return [];
                }),
            );
    }

    private handle(data: FcmMessageDataInterface): void {
        const handlerFunction: pushHelper = this.generatePushHelper[data.action];

        if (!handlerFunction) {
            return;
        }

        this.storeWrapper.clearNotificationData();

        return handlerFunction.call(this, data);
    }

    private dataCollection({ id }: FcmMessageDataInterface): void {
        const redirectUrl = `${RoutesDictionary.DATA_COLLECTION_INSPECTION}/${id}`;

        this.gotoAppPage$(redirectUrl).pipe(take(1)).subscribe();
    }

    private gotoAppPage$(path: string): Observable<boolean> {
        return this.storeWrapper.baseUrl$
            .pipe(
                switchMap((baseUrl: string) => from(this.navigationController.navigateRoot(`${baseUrl}/${path}`))),
            );
    }

    private checkValidProviderId(data: FcmMessageDataInterface, providerId: string): boolean {
        if (!data?.providerId) {
            return true;
        }

        const isCorrectProviderId = data.providerId === providerId;

        if (!isCorrectProviderId) {
            const message = 'This notification is for another app.';

            this.storeWrapper.showToast(message);
        }

        return isCorrectProviderId;
    }

    private isActionHandled({ action }: FcmMessageDataInterface): boolean {
        return Boolean(this.generatePushHelper[action]);
    }

    private preloadDataCollections$(): Observable<unknown> {
        return this.dataCollectionService.rawLoad$()
            .pipe(
                switchMap(({ data: dataCollections = [] }) => this.dbService.saveLoadedDataCollections$(dataCollections)),
            );
    }

    private get isActionSubscriptionEnabled$(): Observable<boolean> {
        const { isAuthenticated$, app$ } = this.storeWrapper;
        const enabledRoles$ = app$
            .pipe(
                map((app: ApplicationsEnum) => Boolean(app)),
            );

        return combineLatest([isAuthenticated$, enabledRoles$])
            .pipe(
                map((keys: Array<boolean>) => keys.every(Boolean)),
            );
    }
}
